import type { AssetContractReference, FundamentalsSnapshot } from '../../types/market';
import { safeNumber } from '../../lib/number';
import { fetchJson } from '../exchanges/http';

interface SearchResponse {
  coins: Array<{ id: string; name: string; symbol: string; market_cap_rank?: number; thumb?: string }>;
}

interface CoinResponse {
  id: string;
  symbol: string;
  name: string;
  image?: { small?: string };
  platforms?: Record<string, string>;
  market_data?: {
    market_cap?: { usd?: number };
    fully_diluted_valuation?: { usd?: number };
    circulating_supply?: number;
    total_supply?: number;
    max_supply?: number;
  };
}

const cache = new Map<string, FundamentalsSnapshot>();

function cleanContracts(platforms?: Record<string, string>): AssetContractReference[] {
  if (!platforms) return [];
  return Object.entries(platforms)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length >= 8)
    .map(([platform, address]) => ({ platform, address: address.trim() }))
    .slice(0, 12);
}

export async function getCoinGeckoFundamentals(baseAsset: string): Promise<FundamentalsSnapshot> {
  const cacheKey = baseAsset.toUpperCase();
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.updatedAt < 15 * 60_000) return cached;

  try {
    const search = await fetchJson<SearchResponse>(
      `https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(baseAsset)}`,
      6_000,
    );
    const matches = search.coins
      .filter((coin) => coin.symbol.toUpperCase() === cacheKey)
      .sort((left, right) => (left.market_cap_rank ?? Number.MAX_SAFE_INTEGER) - (right.market_cap_rank ?? Number.MAX_SAFE_INTEGER));
    const match = matches[0];
    if (!match) throw new Error('No exact symbol match');
    if (matches.length > 1) {
      return {
        symbol: cacheKey,
        source: `Fundamentals withheld because ${matches.length} CoinGecko assets share this ticker; contract identity is required`,
        confidence: 'unavailable',
        updatedAt: Date.now(),
      };
    }

    const detail = await fetchJson<CoinResponse>(
      `https://api.coingecko.com/api/v3/coins/${encodeURIComponent(match.id)}?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false`,
      7_000,
    );
    if (detail.symbol.toUpperCase() !== cacheKey) throw new Error('Resolved asset symbol mismatch');

    const result: FundamentalsSnapshot = {
      name: detail.name,
      symbol: detail.symbol.toUpperCase(),
      imageUrl: detail.image?.small,
      assetId: detail.id,
      marketCap: safeNumber(detail.market_data?.market_cap?.usd, Number.NaN),
      fullyDilutedValue: safeNumber(detail.market_data?.fully_diluted_valuation?.usd, Number.NaN),
      circulatingSupply: safeNumber(detail.market_data?.circulating_supply, Number.NaN),
      totalSupply: safeNumber(detail.market_data?.total_supply, Number.NaN),
      maxSupply: safeNumber(detail.market_data?.max_supply, Number.NaN),
      contracts: cleanContracts(detail.platforms),
      source: `CoinGecko asset metadata · ${detail.id}`,
      confidence: 'high',
      updatedAt: Date.now(),
    };
    for (const key of ['marketCap', 'fullyDilutedValue', 'circulatingSupply', 'totalSupply', 'maxSupply'] as const) {
      if (!Number.isFinite(result[key] ?? Number.NaN)) delete result[key];
    }
    cache.set(cacheKey, result);
    return result;
  } catch {
    return {
      symbol: cacheKey,
      source: 'Fundamentals identity could not be resolved reliably',
      confidence: 'unavailable',
      updatedAt: Date.now(),
    };
  }
}
